import React, { useEffect, useState } from 'react';
import {
  View,
  StyleSheet,
  Pressable,
  ScrollView,
  Platform
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Text, Theme } from '../ui';
import { useAudioPlayer } from '../../contexts/AudioPlayerContext';
import { useNavigation } from '@react-navigation/native';

interface FullAudioPlayerProps {
  showCloseButton?: boolean;
}

const FullAudioPlayer: React.FC<FullAudioPlayerProps> = ({ showCloseButton = false }) => {
  const navigation = useNavigation<any>();
  const {
    currentSegment,
    currentIndex,
    playlist,
    isPlaying,
    isLoading,
    position,
    duration,
    playbackRate,
    book,
    insights,
    playPause,
    seekTo,
    playNext,
    playPrevious,
    jumpToSegment,
    setPlaybackRate,
    hideFullPlayer,
  } = useAudioPlayer();

  const [isDragging, setIsDragging] = useState(false);
  const [dragPosition, setDragPosition] = useState(0);
  const [showSpeedOptions, setShowSpeedOptions] = useState(false);

  // Calculate total duration and segment positions
  const totalDuration = playlist.reduce((acc, segment) => acc + segment.duration, 0);
  
  // Calculate cumulative positions for each segment
  const segmentPositions = playlist.reduce((acc, segment, index) => {
    const previousEnd = index > 0 ? acc[index - 1].end : 0;
    acc.push({
      start: previousEnd,
      end: previousEnd + segment.duration,
      duration: segment.duration,
    });
    return acc;
  }, [] as Array<{ start: number; end: number; duration: number }>);

  // Calculate overall progress
  const currentSegmentPosition = currentIndex >= 0 ? segmentPositions[currentIndex] : null;
  const overallPosition = currentSegmentPosition 
    ? currentSegmentPosition.start + position 
    : 0;
  const overallProgress = totalDuration > 0 ? overallPosition / totalDuration : 0;

  // Format time
  const formatTime = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Handle segment click
  const handleSegmentClick = (index: number) => {
    jumpToSegment(index);
  };

  // Handle seek
  const handleSeek = (newPosition: number) => {
    // Find which segment this position falls into
    let targetIndex = 0;
    let segmentPosition = 0;
    
    for (let i = 0; i < segmentPositions.length; i++) {
      if (newPosition >= segmentPositions[i].start && newPosition <= segmentPositions[i].end) {
        targetIndex = i;
        segmentPosition = newPosition - segmentPositions[i].start;
        break;
      }
    }
    
    if (targetIndex !== currentIndex) {
      jumpToSegment(targetIndex);
    }
    seekTo(segmentPosition);
  };

  // Speed options
  const speedOptions = [0.5, 0.75, 1.0, 1.25, 1.5, 1.75, 2.0];

  // Navigate to content
  const navigateToContent = () => {
    if (!currentSegment) return;
    
    // Only navigate to insights, not introduction
    if (currentSegment.type === 'insight') {
      const insightIndex = insights.findIndex(i => i.id === currentSegment.id);
      navigation.navigate('InsightDetail', {
        insights,
        book,
        bookTitle: book?.title,
        bookCoverUrl: book?.coverImageUrl || book?.coverImageFullUrl,
        currentIndex: insightIndex,
      });
    }
  };

  if (!currentSegment) return null;

  return (
    <View style={styles.container}>
      {/* Current Playing Info with Close Button */}
      <View style={styles.headerContainer}>
        <Pressable style={styles.currentInfo} onPress={navigateToContent}>
          <Text style={styles.currentTitle} numberOfLines={2}>
            {currentSegment.title}
          </Text>
          <Text style={styles.currentSubtitle}>
            {currentSegment.type === 'introduction'
              ? 'Introduction'
              : (() => {
                  const introOffset = playlist.length > 0 && playlist[0].type === 'introduction' ? 1 : 0;
                  return `Insight ${(currentIndex - introOffset) + 1}`;
                })()}
          </Text>
        </Pressable>
        
        {showCloseButton && (
          <Pressable 
            style={styles.closeButton}
            onPress={hideFullPlayer}
          >
            <Ionicons name="close" size={22} color={Theme.colors.textSecondary} />
          </Pressable>
        )}
      </View>

      {/* Segmented Progress Bar */}
      <View style={styles.progressContainer}>
        <View style={styles.segmentedBar}>
          {playlist.map((segment, index) => {
            const segmentWidth = totalDuration > 0 
              ? (segment.duration / totalDuration) * 100 
              : 0;
            const isActive = index === currentIndex;
            const isPast = index < currentIndex;
            
            return (
              <Pressable
                key={segment.id}
                style={[
                  styles.segment,
                  { width: `${segmentWidth}%` },
                  isPast && styles.segmentPast,
                  isActive && styles.segmentActive,
                ]}
                onPress={() => handleSegmentClick(index)}
              >
                {isActive && (
                  <View 
                    style={[
                      styles.segmentProgress,
                      { width: `${(position / segment.duration) * 100}%` }
                    ]}
                  />
                )}
              </Pressable>
            );
          })}
          
          {/* Playhead */}
          <View style={[styles.playhead, { left: `${overallProgress * 100}%` }]} />
        </View>
        
        {/* Time labels */}
        <View style={styles.timeLabels}>
          <Text style={styles.timeText}>{formatTime(overallPosition)}</Text>
          <Text style={styles.timeText}>−{formatTime(totalDuration - overallPosition)}</Text>
        </View>
      </View>

      {/* Main Controls */}
      <View style={styles.mainControls}>
        <Pressable 
          style={styles.skipButton}
          onPress={() => seekTo(Math.max(0, position - 15000))}
        >
          <View style={styles.skipButtonContent}>
            <Ionicons name="refresh" size={16} color={Theme.colors.textPrimary} />
            <Text style={styles.skipText}>15</Text>
          </View>
        </Pressable>

        <Pressable 
          style={styles.controlButton}
          onPress={playPrevious}
          disabled={currentIndex === 0}
        >
          <Ionicons 
            name="play-skip-back" 
            size={28} 
            color={currentIndex === 0 ? Theme.colors.textDisabled : Theme.colors.textPrimary} 
          />
        </Pressable>

        <Pressable 
          style={styles.playButton}
          onPress={playPause}
          disabled={isLoading}
        >
          {isLoading ? (
            <Text style={styles.loadingText}>...</Text>
          ) : (
            <Ionicons 
              name={isPlaying ? "pause" : "play"} 
              size={32} 
              color={Theme.colors.surface} 
            />
          )}
        </Pressable>

        <Pressable 
          style={styles.controlButton}
          onPress={playNext}
          disabled={currentIndex === playlist.length - 1}
        >
          <Ionicons 
            name="play-skip-forward" 
            size={28} 
            color={currentIndex === playlist.length - 1 ? Theme.colors.textDisabled : Theme.colors.textPrimary} 
          />
        </Pressable>

        <Pressable 
          style={styles.skipButton}
          onPress={() => seekTo(Math.min(duration, position + 15000))}
        >
          <View style={[styles.skipButtonContent, styles.skipForward]}>
            <Text style={styles.skipText}>15</Text>
            <Ionicons name="refresh" size={16} color={Theme.colors.textPrimary} />
          </View>
        </Pressable>
      </View>

      {/* Speed Control */}
      <View style={styles.speedControlContainer}>
        {!showSpeedOptions ? (
          <Pressable
            style={styles.currentSpeedButton}
            onPress={() => setShowSpeedOptions(true)}
          >
            <Text style={styles.currentSpeedText}>Speed: {playbackRate}x</Text>
            <Ionicons name="chevron-up" size={16} color={Theme.colors.textSecondary} />
          </Pressable>
        ) : (
          <View>
            <Pressable
              style={styles.closeSpeedButton}
              onPress={() => setShowSpeedOptions(false)}
            >
              <Text style={styles.currentSpeedText}>Speed</Text>
              <Ionicons name="chevron-down" size={16} color={Theme.colors.textSecondary} />
            </Pressable>
            <ScrollView 
              horizontal 
              showsHorizontalScrollIndicator={false}
              style={styles.speedControl}
              contentContainerStyle={styles.speedControlContent}
            >
              {speedOptions.map((speed) => (
                <Pressable
                  key={speed}
                  style={[
                    styles.speedButton,
                    playbackRate === speed && styles.speedButtonActive
                  ]}
                  onPress={() => {
                    setPlaybackRate(speed);
                    setShowSpeedOptions(false);
                  }}
                >
                  <Text 
                    style={{
                      ...styles.speedText,
                      ...(playbackRate === speed ? styles.speedTextActive : {})
                    }}
                  >
                    {speed}x
                  </Text>
                </Pressable>
              ))}
            </ScrollView>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    backgroundColor: Theme.colors.surface,
    paddingTop: Theme.spacing.lg,
    paddingBottom: Platform.OS === 'ios' ? 34 : Theme.spacing.lg,
    paddingHorizontal: Theme.spacing.lg,
    borderTopLeftRadius: Theme.borderRadius.xl,
    borderTopRightRadius: Theme.borderRadius.xl,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.1,
    shadowRadius: 12,
    elevation: 8,
    borderTopWidth: 1,
    borderTopColor: Theme.colors.border,
  },
  headerContainer: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginBottom: Theme.spacing.lg,
    gap: Theme.spacing.md,
  },
  currentInfo: {
    flex: 1,
    alignItems: 'center',
  },
  currentTitle: {
    fontSize: 17,
    fontFamily: Theme.typography.fontFamily.medium,
    color: Theme.colors.textPrimary,
    textAlign: 'center',
    marginBottom: Theme.spacing.xs,
  },
  currentSubtitle: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
  },
  progressContainer: {
    marginBottom: Theme.spacing.xl,
  },
  segmentedBar: {
    height: 8,
    backgroundColor: Theme.colors.border,
    borderRadius: 4,
    flexDirection: 'row',
    overflow: 'hidden',
    position: 'relative',
  },
  segment: {
    height: '100%',
    borderRightWidth: 1,
    borderRightColor: Theme.colors.surface,
    position: 'relative',
  },
  segmentPast: {
    backgroundColor: Theme.colors.primary,
  },
  segmentActive: {
    backgroundColor: Theme.colors.border,
  },
  segmentProgress: {
    position: 'absolute',
    left: 0,
    top: 0,
    bottom: 0,
    backgroundColor: Theme.colors.primary,
  },
  playhead: {
    position: 'absolute',
    top: -4,
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: Theme.colors.primary,
    marginLeft: -8,
  },
  timeLabels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: Theme.spacing.sm,
  },
  timeText: {
    fontSize: 12,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textSecondary,
  },
  mainControls: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: Theme.spacing.lg,
    marginBottom: Theme.spacing.lg,
  },
  controlButton: {
    padding: Theme.spacing.sm,
  },
  playButton: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: Theme.colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: Theme.colors.surface,
    fontSize: 20,
  },
  skipButton: {
    padding: Theme.spacing.sm,
  },
  skipButtonContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 2,
  },
  skipForward: {
    flexDirection: 'row-reverse',
  },
  skipText: {
    fontSize: 12,
    fontFamily: Theme.typography.fontFamily.regular,
    color: Theme.colors.textPrimary,
  },
  speedControl: {
    marginTop: Theme.spacing.sm,
  },
  speedControlContent: {
    flexDirection: 'row',
    gap: Theme.spacing.sm,
    paddingHorizontal: Theme.spacing.lg,
  },
  speedButton: {
    paddingHorizontal: Theme.spacing.md,
    paddingVertical: Theme.spacing.sm,
    borderRadius: Theme.borderRadius.md,
    backgroundColor: Theme.colors.background,
    borderWidth: 1,
    borderColor: Theme.colors.border,
  },
  speedButtonActive: {
    backgroundColor: Theme.colors.primary,
    borderColor: Theme.colors.primary,
  },
  speedText: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.medium,
    color: Theme.colors.textSecondary,
  },
  speedTextActive: {
    color: Theme.colors.surface,
  },
  speedControlContainer: {
    alignItems: 'center',
  },
  currentSpeedButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.xs,
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.sm,
    borderRadius: Theme.borderRadius.md,
    backgroundColor: Theme.colors.background,
    borderWidth: 1,
    borderColor: Theme.colors.border,
  },
  currentSpeedText: {
    fontSize: 14,
    fontFamily: Theme.typography.fontFamily.medium,
    color: Theme.colors.textSecondary,
  },
  closeSpeedButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Theme.spacing.xs,
    paddingHorizontal: Theme.spacing.lg,
    paddingVertical: Theme.spacing.sm,
    marginBottom: Theme.spacing.sm,
    alignSelf: 'center',
  },
  closeButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    justifyContent: 'center',
    alignItems: 'center',
  },
});

export default FullAudioPlayer; 